import * as React from 'react';
import { Link } from 'react-router-dom';
import { AuthDescription } from '../AuthDescription';
import { AuthForm } from '../AuthForm';
import { AuthFormError } from '../AuthFormError';
import { AuthFormRow } from '../AuthFormRow';
import { AuthHeading } from '../AuthHeading';
import {
  AuthRedirectMessage,
  AuthRedirectMessageType
} from '../AuthRedirectMessage';
import { Button } from '../../Forms/Button';
import { FormRow } from '../../Forms/FormRow';
import { SVG } from '../../Utils/SVG';
import { debounce } from '../../../utils/debounce';
import { validateEmail } from '../../../validators/email';
const backIconURL = require('../../../icons/thumbnailback.svg');
import './style.css';

export interface ForgotPasswordProps {
  error: boolean;
  forgotPasswordRequestSentToEmail?: string;
  loading: boolean;
  onForgotPassword: (email: string) => void;
}

export interface ForgotPasswordState {
  email: string;
  emailError?: string;
}

export const GENERATED_PASSWORD_LENGTH = 16;

export class ForgotPassword extends React.Component<
  ForgotPasswordProps,
  ForgotPasswordState
> {
  private debouncedValidateEmail: (email?: string) => void;

  constructor(props: ForgotPasswordProps) {
    super(props);

    this.state = {
      email: ''
    };

    // Debounce to prevent rapid revalidation as user types
    this.debouncedValidateEmail = debounce(this.validateEmail, 200);
  }

  render() {
    const { error, forgotPasswordRequestSentToEmail, loading } = this.props;
    const { email, emailError } = this.state;

    return (
      <main className="forgot-password">
        <AuthHeading>Forgotten Password</AuthHeading>
        <AuthDescription>
          Forgot your password? Enter your email down below and we will send an
          email to the address with instructions on how to reset the password.
        </AuthDescription>
        <AuthForm onSubmit={this.tryForgotPassword}>
          {forgotPasswordRequestSentToEmail ? (
            <FormRow className="forgot-password-request-sent-message-row">
              <div className="forgot-password-request-sent-message">
                A password reset request has been sent to{' '}
                <span className="forgot-password-request-sent-message-email">
                  {forgotPasswordRequestSentToEmail}
                </span>, please check your email for further instructions.
              </div>
            </FormRow>
          ) : (
            <AuthFormRow
              className="forgot-password-form-row"
              inputClassName="forgot-password-input"
              label="Your Email:"
              autoComplete="email"
              value={email}
              error={emailError}
              onChange={event =>
                this.updateEmail((event.target as HTMLInputElement).value)
              }
            />
          )}
          <AuthFormError
            className="edge-of-play-form-row forgot-password-error"
            error={
              error
                ? `Forgot password request failed, please check your email and try again`
                : undefined
            }
          />
          <FormRow className="back-to-login-form-row">
            <Link className="edge-of-play-link back-to-login-link" to="/login">
              <SVG className="back-to-login-link-icon" src={backIconURL} />
              <span>Go back to Log In</span>
            </Link>
          </FormRow>
          <FormRow>
            <Button
              className="forgot-password-button"
              type="submit"
              disabled={loading}
            >
              Request Password Reset
            </Button>
          </FormRow>
          <FormRow>
            <AuthRedirectMessage
              type={AuthRedirectMessageType.RedirectToSignUp}
            />
          </FormRow>
        </AuthForm>
      </main>
    );
  }

  private shouldSubmitBeDisabled = (): boolean => {
    const { loading } = this.props;
    const { email, emailError } = this.state;

    return !!(loading || !email || emailError);
  };

  private updateEmail = (email: string): void => {
    this.debouncedValidateEmail(email);
    this.setState({
      email
    });
  };

  private validateEmail = (email: string): void => {
    this.setState({
      emailError: validateEmail(email)
    });
  };

  private tryForgotPassword = (event: React.FormEvent<HTMLFormElement>) => {
    const { onForgotPassword } = this.props;
    const { email } = this.state;

    event.preventDefault();

    // Force validation before we check if we can submit
    this.validateEmail(email);

    if (onForgotPassword && !this.shouldSubmitBeDisabled()) {
      onForgotPassword(email as string);
    }
  };
}



// WEBPACK FOOTER //
// ./src/app/components/Auth/ForgotPassword/index.tsx